import { Drawer, TextField, Button, Typography, Box, CircularProgress, Autocomplete, IconButton, InputLabel, FormControl, Stack, Divider } from '@mui/material';
import { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { createMenuItem, updateMenuItem, fetchOptionTemplates, createOptionTemplate, deleteOptionTemplate, fetchMenuTypes, createMenuType } from '../../api/restaurantApi';
import ConfirmDialog from '../ui/Modal/ConfirmDialog';

export default function RestaurantMenuFormModal({ open, onClose, initialValues, onSave, restaurantId, propertyId }) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    weight: '',
    price: 0,
    type: 'food',
    options: [] // [{ name: 'Size', choices: [{ name: 'Large', percentage: 10 }] }]
  });
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [menuTypes, setMenuTypes] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({
        name: initialValues?.name || '',
        description: initialValues?.description || '',
        weight: initialValues?.weight || '',
        price: initialValues?.price || 0,
        type: initialValues?.type || 'food',
        options: Array.isArray(initialValues?.options) ? initialValues.options.map(opt => ({
          name: opt?.name || '',
          choices: Array.isArray(opt?.choices) ? opt.choices.map(ch => ({
            name: ch?.name || '',
            percentage: typeof ch?.percentage === 'number' ? ch.percentage : 0
          })) : []
        })) : []
      });
    }
  }, [initialValues, open]);

  useEffect(() => {
    if (open && propertyId) {
      fetchOptionTemplates(propertyId)
        .then(res => setTemplates(res?.data?.templates || []))
        .catch(err => console.error('❌ Failed to fetch option templates:', err));
      
      fetchMenuTypes(propertyId)
        .then(res => setMenuTypes(res?.data?.types || []))
        .catch(err => console.error('❌ Failed to fetch menu types:', err));
    }
  }, [open, propertyId]);

  const addOption = () => {
    setForm(prev => ({ ...prev, options: [...(prev.options || []), { name: '', choices: [] }] }));
  };
  const removeOption = (idx) => {
    setForm(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== idx) }));
  };
  const updateOptionName = (idx, name) => {
    setForm(prev => {
      const options = [...(prev.options || [])];
      options[idx] = { ...(options[idx] || { choices: [] }), name };
      return { ...prev, options };
    });
  };
  const addChoice = (optIdx) => {
    setForm(prev => {
      const options = [...(prev.options || [])];
      const target = options[optIdx] || { name: '', choices: [] };
      target.choices = [...(target.choices || []), { name: '', percentage: 0 }];
      options[optIdx] = target;
      return { ...prev, options };
    });
  };
  const removeChoice = (optIdx, chIdx) => {
    setForm(prev => {
      const options = [...(prev.options || [])];
      const target = options[optIdx];
      if (!target) return prev;
      target.choices = (target.choices || []).filter((_, i) => i !== chIdx);
      options[optIdx] = target;
      return { ...prev, options };
    });
  };
  const updateChoice = (optIdx, chIdx, patch) => {
    setForm(prev => {
      const options = [...(prev.options || [])];
      const target = options[optIdx];
      if (!target) return prev;
      const choices = [...(target.choices || [])];
      choices[chIdx] = { ...choices[chIdx], ...patch };
      target.choices = choices;
      options[optIdx] = target;
      return { ...prev, options };
    });
  };

  const applyTemplateById = (id) => {
    const t = templates.find(x => x._id === id);
    if (!t) return;
    setForm(prev => ({
      ...prev,
      options: [...(prev.options || []), {
        name: t.name,
        choices: (t.choices || []).map(ch => ({
          name: ch?.name || '',
          percentage: Number.isFinite(ch?.percentage) ? ch.percentage : 0
        }))
      }]
    }));
  };

  const saveOptionAsTemplate = async (optIdx) => {
    const opt = form.options?.[optIdx];
    if (!opt) return;
    const name = (opt.name || '').trim();
    if (!name) {
      alert('Please give this option a name before saving as a template.');
      return;
    }
    try {
      const payload = {
        propertyId,
        name,
        choices: (opt.choices || []).map(ch => ({
          name: (ch?.name || '').trim(),
          percentage: Number(ch?.percentage) || 0
        })).filter(ch => ch.name !== '')
      };
      const { data } = await createOptionTemplate(payload);
      setTemplates(prev => [data.template, ...prev]);
    } catch (err) {
      console.error('❌ Failed to create option template:', err);
    }
  };

  const askRemoveTemplate = (id) => {
    setTemplateToDelete(id);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;
    try {
      await deleteOptionTemplate(templateToDelete, propertyId);
      setTemplates(prev => prev.filter(t => t._id !== templateToDelete));
    } catch (err) {
      console.error('❌ Failed to delete option template:', err);
    } finally {
      setConfirmOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // If the type is new, create it first
      const existingType = menuTypes.find(t => t.name === form.type);
      if (!existingType && form.type) {
        const { data } = await createMenuType({ name: form.type, propertyId });
        setMenuTypes(prev => [...prev, data.type]);
      }

      // sanitize options: drop empty names/choices and coerce percentages
      const cleanOptions = (form.options || [])
        .map(opt => ({
          name: (opt.name || '').trim(),
          choices: (opt.choices || [])
            .map(ch => ({
              name: (ch.name || '').trim(),
              percentage: Number.isFinite(ch.percentage) ? ch.percentage : 0
            }))
            .filter(ch => ch.name !== '')
        }))
        .filter(opt => opt.name !== '');

      const payload = { ...form, options: cleanOptions };

      if (initialValues?._id) {
        // update existing item
        const { data } = await updateMenuItem(restaurantId, initialValues._id, payload, propertyId);
        if (onSave) {
          await onSave(data.item); // Pass the updated item from the API response
        }
      } else {
        // create new item
        const { data } = await createMenuItem(restaurantId, payload, propertyId);
        if (onSave) {
          // For creation, we also pass the response item back
          await onSave(data.item);
        }
      }
    } catch (err) {
      console.error('❌ MenuFormModal save failed:', err);
    } finally {
      setSaving(false);
      onClose();
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 500 } }}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">{initialValues?._id ? 'Edit' : 'Add'} Menu Item</Typography>

        <TextField
          fullWidth
          label="Name"
          margin="normal"
          value={form.name}
          onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
        />

        <TextField
          fullWidth
          label="Description"
          margin="normal"
          multiline
          rows={3}
          value={form.description}
          onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
        />

        <TextField
          fullWidth
          label="Weight (e.g., 190g)"
          margin="normal"
          value={form.weight}
          onChange={(e) => setForm(prev => ({ ...prev, weight: e.target.value }))}
        />

        <TextField
          fullWidth
          label="Price"
          margin="normal"
          type="number"
          value={form.price}
          onChange={(e) => setForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
        />

        <Autocomplete
          freeSolo
          options={menuTypes.map(option => option.name)}
          value={form.type}
          onChange={(event, newValue) => {
            setForm(prev => ({ ...prev, type: newValue }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Type"
              margin="normal"
            />
          )}
        />

        <Typography variant="subtitle2" sx={{ mt: 2 }}>Templates</Typography>
        {templates.length > 0 && (
          <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
            {templates.map(t => (
              <Box key={t._id} sx={{ px: 1.25, py: 0.5, border: '1px dashed rgba(0,0,0,0.25)', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">{t.name}</Typography>
                <Button size="small" onClick={() => applyTemplateById(t._id)}>Apply</Button>
                <Button size="small" color="error" onClick={() => askRemoveTemplate(t._id)}>Delete</Button>
              </Box>
            ))}
          </Box>
        )}

        <Box mt={3}>
          <Typography variant="subtitle1" gutterBottom>Options</Typography>
          <Stack spacing={2}>
            {(form.options || []).map((opt, optIdx) => (
              <Box key={optIdx} sx={{ p: 2, borderRadius: 1, border: '1px solid rgba(0,0,0,0.1)' }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    fullWidth
                    label="Option name (e.g., Size, Spiciness)"
                    value={opt.name}
                    onChange={(e) => updateOptionName(optIdx, e.target.value)}
                  />
                  <IconButton aria-label="remove option" onClick={() => removeOption(optIdx)}>
                    <Trash2 size={18} />
                  </IconButton>
                </Box>

                <Box mt={2}>
                  <Typography variant="body2" sx={{ opacity: 0.7, mb: 1 }}>
                    Choices (name + % price adjustment)
                  </Typography>
                  <Stack spacing={1}>
                    {(opt.choices || []).map((ch, chIdx) => (
                      <Box key={chIdx} display="flex" alignItems="center" gap={1}>
                        <TextField
                          label="Choice name"
                          value={ch.name}
                          onChange={(e) => updateChoice(optIdx, chIdx, { name: e.target.value })}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label="%"
                          type="number"
                          value={typeof ch.percentage === 'number' ? ch.percentage : 0}
                          onChange={(e) => updateChoice(optIdx, chIdx, { percentage: parseFloat(e.target.value) || 0 })}
                          sx={{ width: 120 }}
                          inputProps={{ step: '0.1' }}
                        />
                        <IconButton aria-label="remove choice" onClick={() => removeChoice(optIdx, chIdx)}>
                          <Trash2 size={18} />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                  <Box mt={1} display="flex" gap={1}>
                    <Button size="small" startIcon={<Plus size={16} />} onClick={() => addChoice(optIdx)}>
                      Add choice
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => saveOptionAsTemplate(optIdx)}>
                      Save as template
                    </Button>
                  </Box>
                </Box>
              </Box>
            ))}
          </Stack>

          <Box mt={2}>
            <Button startIcon={<Plus size={16} />} onClick={addOption}>
              Add option
            </Button>
          </Box>
        </Box>

        <Box mt={4} display="flex" justifyContent="flex-end">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ ml: 2 }}
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {initialValues?._id ? 'Update' : 'Save'}
          </Button>
        </Box>
      </Box>
      <ConfirmDialog
        open={confirmOpen}
        title="Delete Template"
        description="Are you sure you want to delete this template? This action cannot be undone."
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </Drawer>
  );
}
